import { Link, Navigate, useParams } from "react-router-dom";
import PageHero from "@/components/base/PageHero";
import ScrollReveal from "@/components/base/ScrollReveal";
import SectionHeading from "@/components/base/SectionHeading";
import { RECRUIT_CATEGORIES } from "@/constants/site";

export default function RecruitJobPage() {
  const { categorySlug, jobSlug } = useParams<{ categorySlug: string; jobSlug: string }>();
  const category = RECRUIT_CATEGORIES.find((item) => item.slug === categorySlug);
  const job = category?.jobs.find((item) => item.slug === jobSlug);

  if (!category || !job) {
    return <Navigate to="/recruit" replace />;
  }

  return (
    <div className="w-full bg-background-50">
      <PageHero
        en={job.en.toUpperCase()}
        ja={job.title}
        crumbs={[
          { label: "採用情報", path: "/recruit" },
          { label: category.title, path: `/recruit/${category.slug}` },
          { label: job.title },
        ]}
      />

      <section className="w-full py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <div className="flex flex-col items-center text-center">
              <span className="font-label text-xs tracking-[0.4em] text-secondary-500">
                {category.title}
              </span>
              <h2 className="mt-6 max-w-2xl font-display text-2xl font-light leading-[1.9] tracking-[0.04em] text-foreground-950 md:text-3xl">
                {job.title}
              </h2>
              <p className="mt-6 max-w-2xl font-body text-[15px] leading-[2.2] text-foreground-700">
                {job.summary}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="w-full bg-background-100 py-20 md:py-28">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-20">
          <ScrollReveal>
            <SectionHeading label="募集要項" en="Requirement" />
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <dl className="mt-12 border-t border-background-300/70">
              {job.requirements.map((row) => (
                <div
                  key={row.label}
                  className="grid grid-cols-1 border-b border-background-300/70 md:grid-cols-[220px_1fr]"
                >
                  <dt className="bg-background-50 px-6 py-5 font-body text-sm tracking-[0.08em] text-foreground-700 md:px-8">
                    {row.label}
                  </dt>
                  <dd className="px-6 py-5 font-body text-sm text-foreground-950 md:px-8">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </ScrollReveal>

          <ScrollReveal delay={200}>
            <div className="mt-14 flex flex-col items-center gap-6 border border-foreground-950/10 bg-background-50 px-8 py-14 text-center">
              <span className="font-label text-xs tracking-[0.4em] text-secondary-500">ENTRY</span>
              <h3 className="font-display text-2xl font-light tracking-[0.08em] text-foreground-950 md:text-4xl">
                ENTRY
              </h3>
              <p className="font-body text-sm text-foreground-600">
                まずはお気軽にお問い合わせください。
              </p>
              <Link
                to="/contact"
                className="mt-2 inline-flex items-center gap-3 whitespace-nowrap bg-foreground-950 px-10 py-4 font-body text-[12px] tracking-[0.24em] text-background-50 transition-colors hover:bg-accent-600"
              >
                応募・お問い合わせ
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={240}>
            <div className="mt-10 flex justify-center">
              <Link
                to={`/recruit/${category.slug}`}
                className="inline-flex items-center gap-3 whitespace-nowrap border border-foreground-950 px-9 py-3.5 font-body text-[12px] tracking-[0.22em] text-foreground-950 transition-colors hover:bg-foreground-950 hover:text-background-50"
              >
                <i className="ri-arrow-left-line" />
                {category.title}の募集一覧に戻る
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
